import { normalizeGameFlow } from './gameFlow';
import { fetchApiJson } from './apiRequest.js';
import { readPlayerJersey, normalizeJerseyNumber } from '../data/playerIdentity.js';

const DEFAULT_ERROR = 'Season statistics are temporarily unavailable.';
const LIVE_SEASON = 2026;
const LIVE_CACHE_MS = 2 * 60 * 1000;
const ARCHIVE_CACHE_MS = 60 * 60 * 1000;
const responseCache = new Map();
const pendingRequests = new Map();

const TEAM_LABEL_KEYS = new Map([
  ['kills', 'kills'],
  ['attack errors', 'attackErrors'],
  ['attack attempts', 'attackAttempts'],
  ['hitting %', 'hittingPercentage'],
  ['assists', 'assists'],
  ['aces', 'serviceAces'],
  ['service errors', 'serviceErrors'],
  ['digs', 'digs'],
  ['solo blocks', 'soloBlocks'],
  ['block assists', 'blockAssists'],
  ['total blocks', 'totalBlocks'],
  ['block errors', 'blockErrors'],
  ['reception errors', 'receptionErrors'],
  ['ball-handling errors', 'ballHandlingErrors'],
  ['points', 'points'],
]);

const PLAYER_HEADER_KEYS = new Map([
  ['s', 'setsPlayed'], ['sp', 'setsPlayed'], ['set', 'setsPlayed'], ['sets', 'setsPlayed'],
  ['sets played', 'setsPlayed'], ['set count', 'setsPlayed'], ['sets participated', 'setsPlayed'],
  ['set appearances', 'setsPlayed'], ['gp', 'setsPlayed'], ['games played', 'setsPlayed'],
  ['k', 'kills'], ['kills', 'kills'],
  ['e', 'attackErrors'], ['errors', 'attackErrors'], ['attack errors', 'attackErrors'],
  ['ta', 'attackAttempts'], ['att', 'attackAttempts'], ['attempts', 'attackAttempts'],
  ['pct', 'hittingPercentage'], ['hit%', 'hittingPercentage'], ['hitting %', 'hittingPercentage'],
  ['a', 'assists'], ['assists', 'assists'],
  ['sa', 'serviceAces'], ['aces', 'serviceAces'],
  ['se', 'serviceErrors'], ['service errors', 'serviceErrors'],
  ['d', 'digs'], ['dig', 'digs'], ['digs', 'digs'],
  ['bs', 'soloBlocks'], ['solo blocks', 'soloBlocks'],
  ['ba', 'blockAssists'], ['block assists', 'blockAssists'],
  ['be', 'blockErrors'], ['block errors', 'blockErrors'],
  ['re', 'receptionErrors'], ['reception errors', 'receptionErrors'],
  ['bhe', 'ballHandlingErrors'], ['ball handling errors', 'ballHandlingErrors'],
  ['pts', 'points'], ['points', 'points'],
]);

const SUM_KEYS = [
  'setsPlayed', 'kills', 'attackErrors', 'attackAttempts', 'assists',
  'serviceAces', 'serviceErrors', 'digs', 'soloBlocks', 'blockAssists',
  'blockErrors', 'receptionErrors', 'ballHandlingErrors', 'points',
];

export async function getTeamSeasonStats({ season, team, signal }) {
  const normalizedSeason = Number(season);
  const normalizedTeam = String(team || '').trim();
  if (!Number.isInteger(normalizedSeason) || !normalizedTeam) {
    throw new Error('A team and season are required.');
  }

  const cacheKey = `${normalizedSeason}:${normalizedTeam.toLowerCase()}`;
  const cached = responseCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.data;

  let request = pendingRequests.get(cacheKey);
  if (!request) {
    request = loadTeamSeasonStats({ normalizedSeason, normalizedTeam })
      .then((data) => {
        responseCache.set(cacheKey, {
          data,
          expiresAt: Date.now() + (normalizedSeason === LIVE_SEASON ? LIVE_CACHE_MS : ARCHIVE_CACHE_MS),
        });
        return data;
      })
      .finally(() => pendingRequests.delete(cacheKey));
    pendingRequests.set(cacheKey, request);
  }

  return waitForRequest(request, signal);
}

async function loadTeamSeasonStats({ normalizedSeason, normalizedTeam }) {
  const params = new URLSearchParams({
    season: String(normalizedSeason),
    team: normalizedTeam,
  });
  const { response, payload } = await fetchApiJson(`/api/team-season-stats?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    timeoutMs: 30000,
  });

  if (!response.ok) throw new Error(payload?.message || DEFAULT_ERROR);

  return aggregateSeasonStats(payload, normalizedSeason, normalizedTeam);
}

export function aggregateSeasonStats(payload, fallbackSeason, fallbackTeam) {
  const entries = Array.isArray(payload?.entries) ? payload.entries : [];
  const teamTotals = {};
  const players = new Map();
  let setsPlayed = 0;
  let normalizedGames = 0;
  let gamesWithTeamStats = 0;
  let gamesWithPlayerStats = 0;
  let boxscoresAvailable = 0;

  entries.forEach((entry) => {
    if (!entry?.game) return;
    if (entry?.availability?.boxscore) boxscoresAvailable += 1;

    try {
      const flow = normalizeGameFlow({
        gameId: entry.gameId || entry.game?.gameId,
        source: 'NCAA',
        payloads: entry.payloads || {},
        availability: entry.availability || {},
      }, { game: entry.game });
      const side = entry.game?.teamPerspective?.side === 'home' ? 'home' : 'away';
      const gameSets = numberOrNull(entry.game?.away?.score) !== null && numberOrNull(entry.game?.home?.score) !== null
        ? numberOrNull(entry.game.away.score) + numberOrNull(entry.game.home.score)
        : Math.max(
            Array.isArray(entry.game?.away?.setScores) ? entry.game.away.setScores.length : 0,
            Array.isArray(entry.game?.home?.setScores) ? entry.game.home.setScores.length : 0
          );

      const officialKeys = addTeamComparisons(teamTotals, flow.teamStats, side);
      const table = flow.playerTables.find((item) => item.side === side) || null;
      const hadPlayers = table ? addPlayerTable(players, table) : false;
      const playerGameTotals = table ? derivePlayerTableTotals(table) : {};
      Object.entries(playerGameTotals).forEach(([key, value]) => {
        if (officialKeys.has(key) || !Number.isFinite(value)) return;
        teamTotals[key] = (teamTotals[key] || 0) + value;
      });
      if (officialKeys.size || Object.keys(playerGameTotals).length) gamesWithTeamStats += 1;
      if (hadPlayers) gamesWithPlayerStats += 1;
      if (officialKeys.size || hadPlayers) {
        normalizedGames += 1;
        if (Number.isFinite(gameSets) && gameSets > 0) setsPlayed += gameSets;
      }
    } catch {
      // One malformed NCAA box score should not discard the rest of the season.
    }
  });

  const playerRows = [...players.values()].map(finalizePlayer).sort(comparePlayers);
  const playerDerivedKeys = [
    'kills', 'attackErrors', 'attackAttempts', 'assists', 'serviceAces',
    'serviceErrors', 'digs', 'soloBlocks', 'blockAssists', 'blockErrors',
    'receptionErrors', 'ballHandlingErrors', 'points',
  ];
  playerDerivedKeys.forEach((key) => {
    if (Number.isFinite(teamTotals[key]) || !playerRows.length) return;
    teamTotals[key] = playerRows.reduce((sum, player) => sum + (Number(player[key]) || 0), 0);
  });
  teamTotals.matches = normalizedGames;
  teamTotals.setsPlayed = setsPlayed;
  finalizeTotals(teamTotals);
  const effectiveSets = setsPlayed;

  return {
    season: Number(payload?.season) || fallbackSeason,
    team: payload?.team || { slug: fallbackTeam, name: fallbackTeam },
    fetchedAt: String(payload?.fetchedAt || ''),
    coverage: {
      completedGames: Number(payload?.completedGames) || entries.length,
      scheduledGames:
        Number(payload?.scheduledGames) || Number(payload?.completedGames) || entries.length,
      gamesWithStats: Number(payload?.gamesWithStats) || normalizedGames,
      gamesWithTeamStats,
      gamesWithPlayerStats,
      boxscoresAvailable,
      normalizedGames,
      missingGames: Array.isArray(payload?.missingGames) ? payload.missingGames.map(String) : [],
      complete: entries.length > 0 && normalizedGames >= (Number(payload?.completedGames) || entries.length),
    },
    teamStats: buildTeamStatRows(teamTotals, effectiveSets),
    totals: teamTotals,
    setsPlayed: effectiveSets || 0,
    players: playerRows,
  };
}

function addTeamComparisons(output, comparisons, side) {
  const addedKeys = new Set();
  (Array.isArray(comparisons) ? comparisons : []).forEach((item) => {
    const key = TEAM_LABEL_KEYS.get(normalizeLabel(item?.label));
    if (!key) return;
    const value = parseNumber(side === 'home' ? item?.home : item?.away);
    if (value === null || key === 'hittingPercentage') return;
    output[key] = (output[key] || 0) + value;
    if (['kills', 'attackErrors', 'attackAttempts'].includes(key)) {
      const opponentValue = parseNumber(side === 'home' ? item?.away : item?.home);
      if (opponentValue !== null) {
        const opponentKey = `opponent${key.charAt(0).toUpperCase()}${key.slice(1)}`;
        output[opponentKey] = (output[opponentKey] || 0) + opponentValue;
      }
    }
    addedKeys.add(key);
  });
  return addedKeys;
}

function addPlayerTable(players, table) {
  const rows = normalizePlayerTableRows(table);
  let added = false;
  rows.forEach((row) => {
    const name = row.name;
    if (!name) return;
    const jersey = row.jersey;
    // Number availability can vary between box scores; it must not split totals.
    const key = normalizeLabel(name);
    const current = players.get(key) || {
      name,
      jersey,
      position: row.position,
    };
    if (jersey && current.jersey && jersey !== current.jersey) current.jerseyConflict = true;
    current.jersey = current.jerseyConflict ? '' : current.jersey || jersey;
    row.headers.forEach((header, index) => {
      const statKey = PLAYER_HEADER_KEYS.get(normalizeLabel(header));
      if (!statKey || statKey === 'hittingPercentage') return;
      const value = parseNumber(row.values[index]);
      if (value === null) return;
      current[statKey] = (current[statKey] || 0) + value;
      added = true;
    });
    if (!current.position && row.position) current.position = row.position;
    players.set(key, current);
  });
  return added;
}


function derivePlayerTableTotals(table) {
  const totals = {};
  normalizePlayerTableRows(table).forEach((row) => {
    row.headers.forEach((header, index) => {
      const key = PLAYER_HEADER_KEYS.get(normalizeLabel(header));
      if (!key || key === 'setsPlayed' || key === 'hittingPercentage') return;
      const value = parseNumber(row.values[index]);
      if (value === null) return;
      totals[key] = (totals[key] || 0) + value;
    });
  });
  return totals;
}

function normalizePlayerTableRows(table) {
  const tableHeaders = Array.isArray(table?.headers) ? table.headers.map(String) : [];
  const statHeaders = tableHeaders.length && /player|athlete|name/i.test(tableHeaders[0])
    ? tableHeaders.slice(1)
    : tableHeaders;
  const positions = Array.isArray(table?.positions) ? table.positions : [];

  return (Array.isArray(table?.rows) ? table.rows : [])
    .map((row, index) => {
      if (Array.isArray(row)) {
        const hasPlayerColumn = Boolean(
          tableHeaders.length && /player|athlete|name/i.test(tableHeaders[0])
        );
        return {
          name: String(hasPlayerColumn ? row[0] : '').trim(),
          jersey: normalizeJerseyNumber(table?.jerseys?.[index]),
          position: String(positions[index] || '').trim(),
          headers: statHeaders,
          values: hasPlayerColumn ? row.slice(1) : row,
        };
      }

      if (!row || typeof row !== 'object') return null;
      const values = Array.isArray(row.values)
        ? row.values
        : statHeaders.map((header) => readObjectStat(row, header));
      return {
        name: String(
          row.playerName || row.athleteName || row.fullName || row.displayName || row.name || ''
        ).trim(),
        jersey: readPlayerJersey(row) || normalizeJerseyNumber(table?.jerseys?.[index]),
        position: String(row.position || positions[index] || '').trim(),
        headers: statHeaders,
        values,
      };
    })
    .filter((row) => row?.name && !/team totals?|totals?/i.test(row.name));
}

function readObjectStat(row, header) {
  const target = normalizeLabel(header);
  const direct = Object.entries(row).find(([key]) => normalizeLabel(key) === target);
  if (direct) return direct[1];
  const stats = row?.stats || row?.statistics || {};
  const nested = Object.entries(stats).find(([key]) => normalizeLabel(key) === target);
  return nested?.[1];
}

function finalizeTotals(totals) {
  if (Number.isFinite(totals.attackAttempts) && totals.attackAttempts > 0) {
    totals.hittingPercentage = ((totals.kills || 0) - (totals.attackErrors || 0)) / totals.attackAttempts;
  }
  if (Number.isFinite(totals.soloBlocks) || Number.isFinite(totals.blockAssists)) {
    totals.totalBlocks = (totals.soloBlocks || 0) + (totals.blockAssists || 0) / 2;
  }
  if (Number.isFinite(totals.opponentAttackAttempts) && totals.opponentAttackAttempts > 0) {
    totals.opponentHittingPercentage =
      ((totals.opponentKills || 0) - (totals.opponentAttackErrors || 0)) /
      totals.opponentAttackAttempts;
  }
  if (!Number.isFinite(totals.points)) {
    totals.points = (totals.kills || 0) + (totals.serviceAces || 0) +
      (totals.soloBlocks || 0) + (totals.blockAssists || 0) / 2;
  }
}

function finalizePlayer(player) {
  const output = { ...player };
  if (Number.isFinite(output.attackAttempts) && output.attackAttempts > 0) {
    output.hittingPercentage = ((output.kills || 0) - (output.attackErrors || 0)) / output.attackAttempts;
  } else {
    output.hittingPercentage = null;
  }
  output.totalBlocks = (output.soloBlocks || 0) + (output.blockAssists || 0) / 2;
  if (!Number.isFinite(output.points)) {
    output.points = (output.kills || 0) + (output.serviceAces || 0) + output.totalBlocks;
  }
  SUM_KEYS.forEach((key) => {
    if (!Number.isFinite(output[key])) output[key] = key === 'setsPlayed' ? null : 0;
  });
  return output;
}

function buildTeamStatRows(totals, setsPlayed) {
  const definitions = [
    ['Matches with stats', 'matches', null],
    ['Sets played', 'setsPlayed', null],
    ['Kills', 'kills', 'perSet'],
    ['Attack errors', 'attackErrors', 'perSet'],
    ['Attack attempts', 'attackAttempts', 'perSet'],
    ['Hitting percentage', 'hittingPercentage', 'percentage'],
    ['Opponent hitting percentage', 'opponentHittingPercentage', 'percentage'],
    ['Assists', 'assists', 'perSet'],
    ['Service aces', 'serviceAces', 'perSet'],
    ['Service errors', 'serviceErrors', 'perSet'],
    ['Digs', 'digs', 'perSet'],
    ['Total blocks', 'totalBlocks', 'perSet'],
    ['Reception errors', 'receptionErrors', 'perSet'],
    ['Points', 'points', 'perSet'],
  ];
  const source = { ...totals, setsPlayed };
  return definitions.map(([label, key, secondary]) => {
    const value = source[key];
    return {
      key,
      label,
      value: formatValue(value, secondary === 'percentage' ? 'percentage' : key === 'totalBlocks' || key === 'points' ? 'decimal' : 'count'),
      perSet: secondary === 'perSet' && Number.isFinite(value) && setsPlayed > 0
        ? (value / setsPlayed).toFixed(2)
        : '',
    };
  }).filter((item) => item.value !== '—');
}

function comparePlayers(left, right) {
  return (right.points || 0) - (left.points || 0) ||
    (right.kills || 0) - (left.kills || 0) ||
    left.name.localeCompare(right.name);
}

function parseNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = String(value ?? '').trim().replace(/,/g, '');
  if (!text || text === '—' || text === '-') return null;
  const number = Number(text.replace(/%$/, ''));
  if (!Number.isFinite(number)) return null;
  return text.endsWith('%') ? number / 100 : number;
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeLabel(value) {
  return String(value || '').trim().toLowerCase().replace(/[._]/g, ' ').replace(/\s+/g, ' ');
}

function formatValue(value, format) {
  if (!Number.isFinite(value)) return '—';
  if (format === 'percentage') return value.toFixed(3).replace(/^0\./, '.');
  if (format === 'decimal') return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
  return String(Math.round(value));
}

async function readJson(response) {
  try { return await response.json(); } catch { return null; }
}

function waitForRequest(request, signal) {
  if (!signal) return request;
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    request.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

function abortError() {
  const error = new Error('Aborted');
  error.name = 'AbortError';
  return error;
}
